package org.teddapp.project.ui.login

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.auth.BiometricAuthenticator
import org.teddapp.project.db.repo.AccountRepository
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddLogo
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.adaptive.WithWindowClass
import org.teddapp.project.ui.teddPasswordTransformation
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddType

/**
 * Connexion / création de compte, un seul écran pour toutes les plateformes.
 *
 * Compact : colonne centrée, cercle d'empreinte si la biométrie est disponible.
 * Étendu : pitch à gauche, formulaire à droite. Le formulaire est le même composable
 * dans les deux cas ; seule la mise en page autour change.
 */
@Composable
fun LoginScreen(
    accounts: AccountRepository,
    onLoginSuccess: () -> Unit,
    biometric: BiometricAuthenticator? = null,
    modifier: Modifier = Modifier,
) {
    val model = rememberLoginModel(accounts, biometric, onLoginSuccess)
    WithWindowClass(modifier = modifier) { wc ->
        when (wc) {
            WindowClass.Compact -> LoginCompactLayout(model)
            else -> LoginExpandedLayout(model)
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Étendu (et moyen)
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun LoginExpandedLayout(model: LoginModel) {
    Row(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
        LeftPitch(modifier = Modifier.weight(1f).fillMaxHeight().padding(TeddSpace.S16))
        Box(Modifier.width(1.dp).fillMaxHeight().background(TeddTokens.Line))
        Column(
            modifier = Modifier
                .width(460.dp)
                .fillMaxHeight()
                .background(TeddTokens.Surface)
                .verticalScroll(rememberScrollState())
                .padding(horizontal = TeddSpace.S12, vertical = TeddSpace.S16),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S5, Alignment.CenterVertically),
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                Text(
                    text = if (model.isSignup) "Crée ton compte" else "Content de te revoir",
                    color = TeddTokens.Ink,
                    style = TeddType.Title1,
                )
                Text(
                    text = if (model.isSignup) "Un e-mail et un mot de passe. Ton compte relie tes appareils : ce que tu écris ici se retrouve sur ton téléphone et ton ordinateur."
                    else "Connecte-toi avec ton e-mail et ton mot de passe.",
                    color = TeddTokens.InkMuted,
                    style = TeddType.Body,
                )
            }
            model.errorMessage?.let { ErrorBanner(message = it) }
            LoginForm(model, fieldSpacing = TeddSpace.S4)
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Compact
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun LoginCompactLayout(model: LoginModel) {
    Box(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg).verticalScroll(rememberScrollState())) {
        Column(
            modifier = Modifier.fillMaxSize().padding(horizontal = 28.dp, vertical = 60.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(20.dp),
        ) {
            TeddLogo(size = 56.dp)
            Spacer(modifier = Modifier.height(2.dp))
            PhoneGradientText(
                text = if (model.isSignup) "Crée ton compte" else "Bon retour",
                fontSize = 28.sp,
                fontWeight = FontWeight.Bold,
                letterSpacing = (-0.8).sp,
            )
            Text(
                text = when {
                    model.isSignup -> "Un e-mail et un mot de passe. Ton compte relie tes appareils."
                    model.showBiometric -> "Touche le capteur pour déverrouiller."
                    else -> "Connecte-toi avec ton e-mail."
                },
                color = TeddTokens.InkMuted,
                fontSize = 14.sp,
                textAlign = TextAlign.Center,
                modifier = Modifier.widthIn(max = 320.dp),
            )
            Spacer(modifier = Modifier.height(8.dp))
            model.errorMessage?.let { PhoneErrorBanner(message = it, modifier = Modifier.widthIn(max = 380.dp)) }

            if (model.showBiometric) {
                PhoneBiometricCircle(state = model.scanState, onTap = model::triggerBiometric)
                Spacer(modifier = Modifier.height(8.dp))
                LinkText("Utiliser le mot de passe", onClick = model::usePassword)
            } else {
                Column(modifier = Modifier.fillMaxWidth().widthIn(max = 380.dp), verticalArrangement = Arrangement.spacedBy(14.dp)) {
                    LoginForm(model, fieldSpacing = 14.dp)
                }
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Formulaire commun
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun LoginForm(model: LoginModel, fieldSpacing: androidx.compose.ui.unit.Dp) {
    val pwdTransform = if (model.showPwd) VisualTransformation.None else teddPasswordTransformation()
    Column(verticalArrangement = Arrangement.spacedBy(fieldSpacing)) {
        TeddField(label = "E-mail", value = model.email, onValueChange = model::editEmail, placeholder = "toi@exemple.com")
        TeddField(
            label = "Mot de passe",
            value = model.password,
            onValueChange = model::editPassword,
            visualTransformation = pwdTransform,
            trailingContent = { ShowHideButton(model.showPwd) { model.showPwd = !model.showPwd } },
        )
        if (model.isSignup) {
            TeddField(
                label = "Confirme ton mot de passe",
                value = model.passwordConfirm,
                onValueChange = model::editPasswordConfirm,
                visualTransformation = pwdTransform,
            )
            if (model.passwordConfirm.isNotEmpty() && !model.passwordsMatch) {
                Text("Les mots de passe ne correspondent pas.", color = TeddTokens.Danger, style = TeddType.Caption)
            }
        }
        TeddButton(
            text = when {
                model.busy && model.isSignup -> "Création…"
                model.busy -> "Connexion…"
                model.isSignup -> "Créer mon compte"
                else -> "Se connecter"
            },
            onClick = model::submit,
            kind = TeddButtonKind.Primary,
            size = TeddButtonSize.Lg,
            enabled = model.canSubmit,
            modifier = Modifier.fillMaxWidth(),
        )
        if (!model.isSignup && model.biometricAvailable && model.mode == LoginMode.Password) {
            TeddButton(text = "Revenir à l'empreinte", onClick = model::useBiometric, kind = TeddButtonKind.Ghost, icon = "repeat", modifier = Modifier.fillMaxWidth())
        }
    }
}

@Composable
private fun LinkText(text: String, onClick: () -> Unit, modifier: Modifier = Modifier) {
    Text(
        text = text,
        color = TeddTokens.LimeStrong,
        fontSize = 14.sp,
        fontWeight = FontWeight.SemiBold,
        textAlign = TextAlign.Center,
        modifier = modifier.clickable(
            interactionSource = remember { MutableInteractionSource() },
            indication = null,
            onClick = onClick,
        ),
    )
}
